import React, {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useApolloClient } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { usePhoneValidation } from "@natera/platform/lib/hooks";
import { routes } from "@app/routing";
import { ConfigContext, IntlContext } from "@app/provider";
import { convertPhoneNumberToE164 } from "@app/utils/convertPhoneNumber";
import { classifyCounselingIntent } from "@app/service/intakeForm";
import { useEmbeddedFrameBack } from "@app/hooks";
import {
  GIS_ENTRY_SOURCE_ORDER_LOOKUP,
  GIS_NO_ORDER_UID,
} from "@app/utils/gisEntrySource";
import {
  clearFormStateCache,
  guestIntakeStateCacheKey,
  writeGuestGisSession,
  writeGuestLookupPrefill,
} from "@app/pages/private/scheduleAGeneticInformationSession/context";
import GisOrderPicker, {
  GisOrderPickerOption,
} from "@app/pages/private/gisTriage/GisOrderPicker";
import { HEAP_EVENTS, HeapEventLocation } from "@app/provider/types";
import PublicGisOrderLookupProvider, {
  PublicGisOrderLookupContext,
} from "./provider";
import PublicGisOrderLookupService from "./service";
import LookupStepper, { LookupStepperSubmitPayload } from "./lookupStepper";
import TerminalScreen from "./terminalScreens";
import ScheduleView from "./scheduleView";
import { messages } from "./messages";
import {
  buildLookupPrefilledInput,
  clearLookupMatchedOrders,
  collapseOrdersToLatestPerTestType,
  LOOKUP_STEP_PARAM,
  LOOKUP_STEP_SCHEDULE,
  LOOKUP_STEP_TESTS,
  mapErrorCode,
  readLookupMatchedOrders,
  resolveLookupExternalScheduleUrl,
  resolveLookupFlow,
  writeLookupMatchedOrders,
} from "./helpers";
import {
  PublicGisLookupData,
  PublicGisLookupScreen,
  PublicGisMatchedOrder,
} from "./types";

import "./publicGisOrderLookup.scss";

const Funnel: FC = () => {
  const intl = useIntl();
  const client = useApolloClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { validatePhone } = usePhoneValidation();
  const { config } = useContext(ConfigContext);
  const { currentLanguage } = useContext(IntlContext);
  const {
    lookupData,
    setGuestToken,
    matchedOrder,
    setMatchedOrder,
    matchedOrders,
    setMatchedOrders,
    errorType,
    setErrorType,
  } = useContext(PublicGisOrderLookupContext);

  const step = new URLSearchParams(location.search).get(LOOKUP_STEP_PARAM);

  // The spinner and the terminal error are moments, not places, so they stay
  // out of history and simply cover whichever screen the URL names.
  const [transientScreen, setTransientScreen] = useState<
    PublicGisLookupScreen.MATCHING | PublicGisLookupScreen.ERROR | null
  >(null);

  const screen =
    transientScreen ??
    (step === LOOKUP_STEP_SCHEDULE
      ? PublicGisLookupScreen.WH_OH_SCHEDULE
      : step === LOOKUP_STEP_TESTS
        ? PublicGisLookupScreen.ORDER_SELECT
        : PublicGisLookupScreen.STEPPER);

  // Acuity answers Back with its own scheduling steps from inside the frame,
  // so Back off the scheduler is handed back to the funnel and returns the
  // visitor to the screen that sent them there.
  const leaveSchedule = useEmbeddedFrameBack(
    screen === PublicGisLookupScreen.WH_OH_SCHEDULE
  );

  const goToStep = useCallback(
    (next: string) => {
      setTransientScreen(null);
      navigate({ search: `?${LOOKUP_STEP_PARAM}=${next}` });
    },
    [navigate]
  );

  // Back and Forward land on a screen of their own, retiring whatever moment
  // the visitor left behind. Screen changes the funnel makes itself clear it
  // as they navigate, so this only catches the ones the browser drives.
  useEffect(() => {
    setTransientScreen(null);
  }, [step]);

  // Back out of the intake mounts the funnel afresh on the URL the visitor
  // left it at, without the state that filled that screen. The list behind the
  // picker outlives the trip; anything else reopens at the form, which still
  // holds their answers.
  useEffect(() => {
    if (!step) return;

    if (step === LOOKUP_STEP_TESTS) {
      const orders = readLookupMatchedOrders();
      if (orders.length >= 2) {
        setMatchedOrders(orders);
        return;
      }
    }

    navigate({ search: "" }, { replace: true });
  }, []);

  const fail = useCallback(
    (code?: string) => {
      setErrorType(mapErrorCode(code));
      setTransientScreen(PublicGisLookupScreen.ERROR);
    },
    [setErrorType]
  );

  const createGuestSession = useCallback(
    async (order: PublicGisMatchedOrder, lookupData?: PublicGisLookupData) => {
      try {
        await client.mutate({
          mutation: PublicGisOrderLookupService.createOncUppGisTriageNonAuth(),
          variables: {
            token: order.token,
            orderUidPublic: order.orderUidPublic,
            testUid: order.testUid,
            schedulingSource: GIS_ENTRY_SOURCE_ORDER_LOOKUP,
            prefilledInput: buildLookupPrefilledInput(
              lookupData,
              validatePhone
            ),
          },
        });
      } catch {
        // Session creation is for attribution/analytics only — never block the
        // patient's path to scheduling on it.
      }
    },
    [client, validatePhone]
  );

  const routeMatchedOrder = useCallback(
    async (order: PublicGisMatchedOrder, lookupData?: PublicGisLookupData) => {
      // Each matched order carries its own order-bound guest token, so the
      // token always authorizes the downstream NonAuth GIS + Acuity calls for
      // exactly the order the patient is scheduling.
      setGuestToken(order.token);
      setMatchedOrder(order);
      await createGuestSession(order, lookupData);

      const externalScheduleUrl = resolveLookupExternalScheduleUrl(
        order,
        config,
        currentLanguage
      );
      if (externalScheduleUrl) {
        heap.track(HEAP_EVENTS.upp_click_gis, {
          location: HeapEventLocation.gis_triage_page,
          test_type: order.testType,
          source: GIS_ENTRY_SOURCE_ORDER_LOOKUP,
        });
        window.location.assign(externalScheduleUrl);
        return;
      }

      const flow = resolveLookupFlow(order);
      if (flow === "ONC") {
        // ONC patients run the full intake flow ending in the custom calendar
        // scheduler. The guest session (token + matched-order meta) is stashed
        // in sessionStorage so it survives the navigation into the intake
        // routes, which mount a fresh component tree under the guest layout.
        writeGuestGisSession(order.orderUidPublic, order.testUid, {
          token: order.token,
          testType: order.testType,
          status: order.status,
          counselingIntent:
            order.counselingIntent ?? classifyCounselingIntent(order.status),
          limsCaseId: order.limsCaseId,
        });
        // Carry the contact details the visitor just typed so confirm-info can
        // still prefill phone/email if the server-side order prefill is empty
        // or fails (e.g. SOS schema drift on non-prod).
        writeGuestLookupPrefill({
          firstName: lookupData?.firstName || undefined,
          lastName: lookupData?.lastName || undefined,
          dateOfBirth: lookupData?.dateOfBirth || undefined,
          phone: lookupData?.mobileNumber
            ? convertPhoneNumberToE164(lookupData.mobileNumber)
            : undefined,
          email: lookupData?.email || undefined,
          zipCode: lookupData?.zipCode || undefined,
        });
        // Start this attempt from a clean slate: a prior intake for the same
        // cache key may have persisted `bookingConfirmed`, which would bounce
        // the visitor out of the intake steps on their next visit.
        clearFormStateCache(
          guestIntakeStateCacheKey(order.orderUidPublic, order.testUid)
        );
        navigate(
          routes.scheduleAGeneticInformationSessionIntakePage(
            order.orderUidPublic,
            order.testUid
          )
        );
        return;
      }
      // WH/OH (Empower) patients keep Acuity's hosted iframe scheduler, on an
      // entry of its own so Back off it returns to whichever screen sent them
      // there — the picker for a visitor who chose among their tests, the form
      // for everyone else.
      goToStep(LOOKUP_STEP_SCHEDULE);
    },
    [
      config,
      createGuestSession,
      currentLanguage,
      goToStep,
      navigate,
      setGuestToken,
      setMatchedOrder,
    ]
  );

  const routeNoOrder = useCallback(
    async (token: string, lookupData?: PublicGisLookupData) => {
      // Patient matched but has no GIS-eligible order. Instead of dead-ending
      // at the terminal screen, route them into the no-order guest intake flow
      // (the anonymous equivalent of the authenticated `GIS_NO_ORDER_UID` path
      // in PublicGisTriage). The server minted a patient-scoped token bound to
      // the `GIS_NO_ORDER_UID` sentinel that authorizes the NonAuth surface.
      setGuestToken(token);

      // Create the no-order triage row up-front (sentinel uids) so the
      // downstream intake steps can attach data to it and funnel analytics are
      // recorded. Never block the patient's path on it — the intake steps also
      // look up / update the session.
      try {
        await client.mutate({
          mutation: PublicGisOrderLookupService.createOncUppGisTriageNonAuth(),
          variables: {
            token,
            orderUidPublic: GIS_NO_ORDER_UID,
            testUid: GIS_NO_ORDER_UID,
            schedulingSource: GIS_ENTRY_SOURCE_ORDER_LOOKUP,
            prefilledInput: buildLookupPrefilledInput(
              lookupData,
              validatePhone
            ),
          },
        });
      } catch {
        // Session creation is for attribution/analytics only — never block the
        // patient's path to scheduling on it.
      }

      // Stash the guest session (token) keyed by the no-order sentinel so the
      // guest intake layout can authorize the NonAuth flow after navigation
      // into a fresh component tree.
      writeGuestGisSession(GIS_NO_ORDER_UID, GIS_NO_ORDER_UID, { token });

      // The no-order cache key is a static sentinel shared by every no-order
      // attempt, so a prior confirmed booking's persisted `bookingConfirmed`
      // flag would otherwise rehydrate and immediately redirect this fresh
      // attempt out of the intake steps (to home → guest login). Clear it.
      clearFormStateCache(
        guestIntakeStateCacheKey(GIS_NO_ORDER_UID, GIS_NO_ORDER_UID)
      );

      // Carry the details the visitor just entered in the lookup into the
      // no-order confirm-info form so it's prefilled (there's no matched
      // order — and often no matched patient — to prefill from server-side).
      writeGuestLookupPrefill({
        firstName: lookupData?.firstName || undefined,
        lastName: lookupData?.lastName || undefined,
        dateOfBirth: lookupData?.dateOfBirth || undefined,
        phone: lookupData?.mobileNumber
          ? convertPhoneNumberToE164(lookupData.mobileNumber)
          : undefined,
        email: lookupData?.email || undefined,
        zipCode: lookupData?.zipCode || undefined,
      });

      // Heap parity with the authenticated no-order path (PublicGisTriage fires
      // the same view event), attributed to the order-lookup funnel.
      heap.track(HEAP_EVENTS.upp_view_gis_triage, {
        location: HeapEventLocation.gis_triage_page,
        source: GIS_ENTRY_SOURCE_ORDER_LOOKUP,
      });

      navigate(
        routes.scheduleAGeneticInformationSessionIntakePage(
          GIS_NO_ORDER_UID,
          GIS_NO_ORDER_UID
        )
      );
    },
    [client, navigate, setGuestToken, validatePhone]
  );

  const runMatch = useCallback(
    async ({
      lookupData,
      lookupIdentifier,
      recaptchaValue,
    }: LookupStepperSubmitPayload) => {
      if (!lookupData || !recaptchaValue) {
        fail();
        return;
      }

      // Whatever this match turns up replaces the list a previous one left
      // behind, so no stale set of tests can be reopened later.
      clearLookupMatchedOrders();
      setTransientScreen(PublicGisLookupScreen.MATCHING);

      const patientNonAuthInfoData = {
        firstName: lookupData.firstName,
        lastName: lookupData.lastName,
        alternativeLastName: lookupData.alternativeLastName,
        dateOfBirth: lookupData.dateOfBirth,
        email: lookupIdentifier === "email" ? lookupData.email : "",
        phone:
          lookupIdentifier === "mobileNumber"
            ? convertPhoneNumberToE164(lookupData.mobileNumber)
            : "",
        zipCode: lookupIdentifier === "zipCode" ? lookupData.zipCode : "",
      };

      try {
        const { data } = await client.query({
          query: PublicGisOrderLookupService.matchGisOrdersNonAuth(),
          // The lookup form challenges with an always-visible v2 checkbox
          // rather than a v3 score, so the value it produces travels in the
          // v2 slot the resolver's verifySubmit branches on.
          variables: {
            patientNonAuthInfoData,
            recaptchaV2Token: recaptchaValue,
          },
          fetchPolicy: "no-cache",
        });
        const res = data?.matchGisOrdersNonAuth;
        // A successful match returns no code; any code means the lookup turned
        // up nothing to schedule against.
        if (res?.code) {
          // Any "couldn't find eligible results" outcome that still returns a
          // guest token routes into the no-order scheduling form instead of
          // dead-ending at the terminal error. The server answers with the same
          // MP_NO_ORDER_FOUND whether the patient matched with no eligible order
          // or no patient matched at all, deliberately: telling the two apart
          // would let an anonymous caller confirm who is a Natera patient.
          if (res.token) {
            await routeNoOrder(res.token, lookupData);
            return;
          }
          fail(res.code);
          return;
        }

        // Routing is driven by the eligible-order list, collapsed to the latest
        // order per test type (same rule as the authenticated picker). Each
        // surviving order carries its own order-bound token.
        const orders = collapseOrdersToLatestPerTestType(
          ((res?.orders ?? []) as PublicGisMatchedOrder[]).filter(
            (order) => order.orderUidPublic && order.testUid && order.token
          )
        );

        if (orders.length === 0) {
          fail("MP_NO_ORDER_FOUND");
          return;
        }

        if (orders.length === 1) {
          await routeMatchedOrder(orders[0], lookupData);
          return;
        }

        // Multiple distinct products — let the patient choose which one to
        // schedule the genetic information session for. Lookup demographics are
        // already in context by the time the picker resolves (setState from the
        // stepper has committed while MATCHING rendered).
        setMatchedOrders(orders);
        // Kept for the trip through the intake, which Back can return from.
        writeLookupMatchedOrders(orders);
        heap.track(HEAP_EVENTS.upp_view_gis_order_select, {
          location: HeapEventLocation.gis_order_select_page,
          source: GIS_ENTRY_SOURCE_ORDER_LOOKUP,
        });
        goToStep(LOOKUP_STEP_TESTS);
      } catch (error) {
        fail(
          error?.graphQLErrors?.[0]?.extensions?.code ??
            error?.code ??
            undefined
        );
      }
    },
    [client, fail, routeMatchedOrder, routeNoOrder, setMatchedOrders]
  );

  const pickerOrders = useMemo<GisOrderPickerOption[]>(
    () =>
      matchedOrders.map((order) => ({
        id: order.orderUidPublic,
        testType: order.testType,
        createdAt: order.createdAt,
      })),
    [matchedOrders]
  );

  const handleOrderSelected = useCallback(
    (orderUidPublic: string) => {
      const order = matchedOrders.find(
        (candidate) => candidate.orderUidPublic === orderUidPublic
      );
      if (!order) {
        fail("MP_GENERIC_NO_MATCH_ERROR");
        return;
      }
      heap.track(HEAP_EVENTS.upp_gis_order_select, {
        location: HeapEventLocation.gis_order_select_page,
        source: GIS_ENTRY_SOURCE_ORDER_LOOKUP,
        test_type: order.testType,
        status: order.status,
      });
      void routeMatchedOrder(order, lookupData);
    },
    [fail, lookupData, matchedOrders, routeMatchedOrder]
  );

  const restart = useCallback(() => {
    setErrorType("MP_GENERIC_NO_MATCH_ERROR");
    setTransientScreen(null);
    // Retrying is the same visit, not a step forward, so it takes the entry
    // the failure happened on rather than stacking another.
    if (step) navigate({ search: "" }, { replace: true });
  }, [navigate, setErrorType, step]);

  // The lookup form is the only screen this list is ever reached from, so Back
  // names it. A visitor who reached the list by backing out of the intake has
  // those screens behind them in history, not the form.
  const goToLookupForm = useCallback(
    () => navigate({ search: "" }),
    [navigate]
  );

  const renderScreen = () => {
    switch (screen) {
      case PublicGisLookupScreen.MATCHING:
        return (
          <div className="public-gis-lookup__matching" aria-live="polite">
            <p>{intl.formatMessage(messages.matching)}</p>
          </div>
        );
      case PublicGisLookupScreen.ERROR:
        return (
          <TerminalScreen
            errorType={errorType ?? "MP_GENERIC_NO_MATCH_ERROR"}
            onRetry={restart}
          />
        );
      case PublicGisLookupScreen.ORDER_SELECT:
        return (
          <GisOrderPicker
            orders={pickerOrders}
            onSelect={handleOrderSelected}
            onBack={goToLookupForm}
          />
        );
      case PublicGisLookupScreen.WH_OH_SCHEDULE:
        return matchedOrder ? (
          <ScheduleView matchedOrder={matchedOrder} onBack={leaveSchedule} />
        ) : (
          <TerminalScreen
            errorType="MP_GENERIC_NO_MATCH_ERROR"
            onRetry={restart}
          />
        );
      case PublicGisLookupScreen.STEPPER:
      default:
        return <LookupStepper onSubmit={runMatch} />;
    }
  };

  // The funnel, not the screen it happens to be showing, owns the page: every
  // screen from the lookup form through the test list reads on the same tinted
  // background the intake continues.
  return <div className="public-gis-lookup__page">{renderScreen()}</div>;
};

/**
 * Public entry point for the anonymous order-lookup (Public_Entry_Order_Lookup)
 * GIS funnel. Wraps the funnel in its own provider so all state is scoped to
 * this route and discarded on unmount/reload.
 */
const PublicGisOrderLookup: FC = () => (
  <PublicGisOrderLookupProvider>
    <Funnel />
  </PublicGisOrderLookupProvider>
);

export default PublicGisOrderLookup;
